/**
 * Incognito Mode Detection & Browser Storage Utilities
 *
 * CRITICAL: Bug #4 Fix - Two-Method Detection
 * Safari with "Prevent Cross-Site Tracking" has low quota but high usage
 * We must check BOTH quota value AND usage ratio
 *
 * FEATURES:
 * - Two-method incognito detection (quota + IndexedDB)
 * - 2-second timeout for Safari hangs
 * - Cleanup test databases (Bug #19)
 * - Persistent storage request with user explanation (Bug #16)
 */

import { devLog } from './devLog';

// Detection cache (avoid repeated checks)
let incognitoDetectionCache = null;
let incognitoDetectionTimestamp = null;
const CACHE_DURATION = 60000; // 1 minute

/**
 * Detect if browser is in incognito/private mode
 *
 * Bug #4 Fix: Two-method detection
 * - Method 1: Storage quota check (ratio, not absolute value)
 * - Method 2: IndexedDB write test
 *
 * @returns {Promise<{isIncognito: boolean, method: string, details: Object}>}
 */
export async function detectIncognitoMode() {
  try {
    // Return cached result if fresh
    if (
      incognitoDetectionCache !== null &&
      incognitoDetectionTimestamp &&
      Date.now() - incognitoDetectionTimestamp < CACHE_DURATION
    ) {
      devLog('[INCOGNITO] Returning cached result:', incognitoDetectionCache);
      return incognitoDetectionCache;
    }

    // Run both methods with timeout (Bug #4)
    const result = await Promise.race([
      detectWithBothMethods(),
      createTimeout(2000, { isIncognito: false, method: 'timeout', details: { error: 'Detection timed out' } })
    ]);

    // Cache result
    incognitoDetectionCache = result;
    incognitoDetectionTimestamp = Date.now();

    return result;

  } catch (error) {
    console.error('[INCOGNITO] Detection failed:', error);
    return {
      isIncognito: false,
      method: 'error',
      details: { error: error.message }
    };
  }
}

/**
 * Run both detection methods and combine results
 *
 * @returns {Promise<{isIncognito: boolean, method: string, details: Object}>}
 */
async function detectWithBothMethods() {
  const quotaResult = await detectViaQuota();
  const idbResult = await detectViaIndexedDB();

  // If BOTH methods indicate incognito, it's likely true
  if (quotaResult.isIncognito && idbResult.isIncognito) {
    devLog('[INCOGNITO] Both methods detected incognito mode');
    return {
      isIncognito: true,
      method: 'both',
      details: {
        quota: quotaResult.details,
        indexedDB: idbResult.details
      }
    };
  }

  // If EITHER method is confident, trust it
  if (quotaResult.isIncognito && quotaResult.details.confidence === 'high') {
    devLog('[INCOGNITO] Quota method detected incognito (high confidence)');
    return quotaResult;
  }

  if (idbResult.isIncognito && idbResult.details.confidence === 'high') {
    devLog('[INCOGNITO] IndexedDB method detected incognito (high confidence)');
    return idbResult;
  }

  // Default: Not incognito
  devLog('[INCOGNITO] No incognito detected');
  return {
    isIncognito: false,
    method: 'combined',
    details: {
      quota: quotaResult.details,
      indexedDB: idbResult.details
    }
  };
}

/**
 * Method 1: Storage Quota Detection (Bug #4 - Check RATIO not absolute value)
 *
 * Safari with "Prevent Cross-Site Tracking":
 * - quota: ~10MB
 * - usage: ~8MB (high usage ratio)
 * - This is LEGITIMATE, not incognito
 *
 * Incognito Safari:
 * - quota: ~10MB
 * - usage: ~0 bytes (low usage ratio)
 * - This is INCOGNITO
 *
 * @returns {Promise<{isIncognito: boolean, method: string, details: Object}>}
 */
async function detectViaQuota() {
  try {
    if (!navigator.storage || !navigator.storage.estimate) {
      return {
        isIncognito: false,
        method: 'quota',
        details: { error: 'Storage API not supported', confidence: 'low' }
      };
    }

    const estimate = await navigator.storage.estimate();
    const quota = estimate.quota || 0;
    const usage = estimate.usage || 0;
    const usageRatio = quota > 0 ? usage / quota : 0;

    devLog('[INCOGNITO] Quota check:', {
      quota: (quota / 1024 / 1024).toFixed(2) + ' MB',
      usage: (usage / 1024 / 1024).toFixed(2) + ' MB',
      usageRatio: (usageRatio * 100).toFixed(2) + '%'
    });

    // Bug #4 Fix: Check RATIO, not absolute value
    // If quota is suspiciously low AND usage is near zero, likely incognito
    const hasLowQuota = quota < 120 * 1024 * 1024; // < 120MB
    const hasLowUsage = usageRatio < 0.01; // < 1% used

    if (hasLowQuota && hasLowUsage) {
      return {
        isIncognito: true,
        method: 'quota',
        details: {
          quota,
          usage,
          usageRatio,
          reason: 'Low quota with near-zero usage',
          confidence: 'high'
        }
      };
    }

    // Safari with tracking prevention has low quota but HIGH usage
    if (hasLowQuota && usageRatio > 0.1) {
      return {
        isIncognito: false,
        method: 'quota',
        details: {
          quota,
          usage,
          usageRatio,
          reason: 'Low quota but high usage (legitimate user)',
          confidence: 'high'
        }
      };
    }

    return {
      isIncognito: false,
      method: 'quota',
      details: {
        quota,
        usage,
        usageRatio,
        reason: 'Normal quota detected',
        confidence: 'medium'
      }
    };

  } catch (error) {
    return {
      isIncognito: false,
      method: 'quota',
      details: { error: error.message, confidence: 'low' }
    };
  }
}

/**
 * Method 2: IndexedDB Write Test
 *
 * Incognito mode often blocks or restricts IndexedDB writes
 *
 * @returns {Promise<{isIncognito: boolean, method: string, details: Object}>}
 */
async function detectViaIndexedDB() {
  try {
    if (!window.indexedDB) {
      return {
        isIncognito: false,
        method: 'indexedDB',
        details: { error: 'IndexedDB not supported', confidence: 'low' }
      };
    }

    // Try to open a test database
    const testDbName = 'sr-incognito-test';
    const request = indexedDB.open(testDbName, 1);

    const result = await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('IndexedDB open timeout'));
      }, 1000);

      request.onsuccess = () => {
        clearTimeout(timeout);
        const db = request.result;

        // Cleanup test database (Bug #19)
        db.close();
        indexedDB.deleteDatabase(testDbName);

        resolve({
          isIncognito: false,
          method: 'indexedDB',
          details: { success: true, confidence: 'medium' }
        });
      };

      request.onerror = () => {
        clearTimeout(timeout);

        // Cleanup attempt
        try {
          indexedDB.deleteDatabase(testDbName);
        } catch (e) {
          // Ignore cleanup errors
        }

        resolve({
          isIncognito: true,
          method: 'indexedDB',
          details: {
            error: request.error?.message || 'IndexedDB blocked',
            confidence: 'high'
          }
        });
      };

      request.onupgradeneeded = () => {
        // Just create the database, no stores needed
      };
    });

    return result;

  } catch (error) {
    return {
      isIncognito: false,
      method: 'indexedDB',
      details: { error: error.message, confidence: 'low' }
    };
  }
}

/**
 * Create timeout promise
 * @param {number} ms - Milliseconds to wait
 * @param {any} value - Value to resolve with
 * @returns {Promise}
 */
function createTimeout(ms, value) {
  return new Promise(resolve => setTimeout(() => resolve(value), ms));
}

/**
 * Request persistent storage permission (Bug #16)
 *
 * Shows user explanation before requesting permission
 * @returns {Promise<{granted: boolean, persisted: boolean}>}
 */
export async function requestPersistentStorage() {
  try {
    if (!navigator.storage || !navigator.storage.persist) {
      devLog('[STORAGE] Persistent storage not supported');
      return { granted: false, persisted: false };
    }

    // Check if already persisted
    const isPersisted = await navigator.storage.persisted();
    if (isPersisted) {
      devLog('[STORAGE] Storage already persisted');
      return { granted: true, persisted: true };
    }

    // Request persistence
    const granted = await navigator.storage.persist();

    devLog('[STORAGE] Persistent storage request:', { granted });
    return { granted, persisted: granted };

  } catch (error) {
    console.error('[STORAGE] Error requesting persistent storage:', error);
    return { granted: false, persisted: false };
  }
}

/**
 * Check if storage is persisted
 * @returns {Promise<boolean>}
 */
export async function isStoragePersisted() {
  try {
    if (!navigator.storage || !navigator.storage.persisted) {
      return false;
    }

    return await navigator.storage.persisted();
  } catch (error) {
    console.error('[STORAGE] Error checking persistence:', error);
    return false;
  }
}

/**
 * Clear incognito detection cache (for testing)
 */
export function clearDetectionCache() {
  incognitoDetectionCache = null;
  incognitoDetectionTimestamp = null;
  devLog('[INCOGNITO] Detection cache cleared');
}

/**
 * Get storage estimate (for debugging)
 * @returns {Promise<{quota: number, usage: number, usageRatio: number}>}
 */
export async function getStorageEstimate() {
  try {
    if (!navigator.storage || !navigator.storage.estimate) {
      return { quota: 0, usage: 0, usageRatio: 0 };
    }

    const estimate = await navigator.storage.estimate();
    const quota = estimate.quota || 0;
    const usage = estimate.usage || 0;
    const usageRatio = quota > 0 ? usage / quota : 0;

    return { quota, usage, usageRatio };
  } catch (error) {
    console.error('[STORAGE] Error getting estimate:', error);
    return { quota: 0, usage: 0, usageRatio: 0 };
  }
}
